import { useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import {
  Box, Typography, Paper, TextField, Button, CircularProgress,
  InputAdornment, IconButton, Alert
} from '@mui/material'
import { Visibility, VisibilityOff, LockReset } from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import { apiClient } from '@/services/apiClient'

export default function ResetPassword() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()

  const userId = searchParams.get('userId') ?? ''
  const token = searchParams.get('token') ?? ''

  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [saving, setSaving] = useState(false)
  const [success, setSuccess] = useState(false)
  const [errors, setErrors] = useState<string[]>([])

  const isInvalid = !userId || !token

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (newPassword !== confirmPassword) {
      enqueueSnackbar('Passwords do not match', { variant: 'error' })
      return
    }
    setSaving(true)
    setErrors([])
    try {
      await apiClient.post('/api/auth/reset-password', { userId, token, newPassword })
      setSuccess(true)
      enqueueSnackbar('Password reset successfully', { variant: 'success' })
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to reset password'
      try {
        const parsed = JSON.parse(message)
        if (parsed.errors) {
          setErrors(parsed.errors)
          return
        }
      } catch {
        // not JSON
      }
      setErrors([message])
    } finally {
      setSaving(false)
    }
  }

  if (isInvalid) {
    return (
      <Box sx={{ maxWidth: 400, mx: 'auto', mt: 4 }}>
        <Alert severity="error">
          Invalid or missing reset link parameters. Please request a new password reset link.
        </Alert>
        <Button sx={{ mt: 2 }} onClick={() => navigate('/login')}>
          Back to Login
        </Button>
      </Box>
    )
  }

  return (
    <Box sx={{ maxWidth: 400, mx: 'auto', mt: 4 }}>
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <LockReset color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Set New Password
          </Typography>
        </Box>

        {success ? (
          <Box>
            <Alert severity="success" sx={{ mb: 2 }}>
              Your password has been reset successfully.
            </Alert>
            <Button variant="contained" fullWidth onClick={() => navigate('/login')}>
              Go to Login
            </Button>
          </Box>
        ) : (
          <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {errors.length > 0 && (
              <Alert severity="error">
                {errors.map((e, i) => <div key={i}>{e}</div>)}
              </Alert>
            )}

            <TextField
              label="New Password"
              type={showPassword ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              fullWidth
              size="small"
              disabled={saving}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setShowPassword(!showPassword)} edge="end">
                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />

            <TextField
              label="Confirm New Password"
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              fullWidth
              size="small"
              disabled={saving}
              error={confirmPassword.length > 0 && newPassword !== confirmPassword}
              helperText={
                confirmPassword.length > 0 && newPassword !== confirmPassword
                  ? 'Passwords do not match'
                  : ''
              }
            />

            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={saving || !newPassword || newPassword !== confirmPassword}
            >
              {saving ? <CircularProgress size={20} /> : 'Reset Password'}
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  )
}
